import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import {
  EditorialRatingsEntities,
  EditorialRatingsV4Entities,
  EditorialReviewEntities,
} from 'client/data/models/editorial-review';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import {
  addRatingsV4NamesAndQuestions,
  getModelSectionMap,
} from 'site-modules/shared/utils/core-page/editorial-review';
import { getOverallRating } from 'site-modules/shared/utils/editorial-helper';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { Link } from 'site-modules/shared/components/link/link';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { hasExplanation, RatingBadge } from 'site-modules/core-page/components/rating-badge/rating-badge';
import { EdmundsTestedBadge } from 'site-modules/core-page/components/edmunds-tested-badge/edmunds-tested-badge';
import { ArticleAuthor } from 'site-modules/shared/components/preprod-articles/article-author';
import { PricingAd } from 'site-modules/shared/components/native-ad/pricing-ad/pricing-ad';

import { RatingScorecardSection } from './rating-scorecard-section';

import './rating-scorecard.scss';

const VERDICT_KEY = 'verdict';

export function RatingScorecard({
  params = null,
  vehicleParams = null,
  ratings = null,
  ratingsV4 = null,
  scale = 10,
  edmundsTypeCategories = null,
  ratedBy = null,
  expandedEntryId,
  embedInteriorPhoto = false,
  scorecardVersion = null,
  hasCarryoverDisclaimer = false,
  isScorecardV4 = false,
  isMobile = false,
  isLargeHeadings = false, // TODO: Review after core-7747
}) {
  const overallRating = getOverallRating({ ratings, ratingsV4 });
  if (!overallRating) {
    return null;
  }

  const { makeName, modelName, year } = vehicleParams;
  const modelSectionMap =
    addRatingsV4NamesAndQuestions({ sections: ratingsV4?.ratingSections, params }) ||
    getModelSectionMap({
      makeName,
      modelName,
      edmundsTypeCategories,
      params,
      isScorecardV4,
    });
  const renderMap = [{ key: VERDICT_KEY }, ...modelSectionMap];
  const vehicleName = hasCarryoverDisclaimer ? `${makeName} ${modelName}` : `${year} ${makeName} ${modelName}`;

  const ourVerdict = (
    <ScrollElement id={MENU_HASH.VERDICT} key={VERDICT_KEY}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-0_75">
        <h2
          className={classnames('text-nowrap me-1 mb-0 py-0_5', {
            'heading-4': !isLargeHeadings,
            'heading-3': isLargeHeadings,
          })}
        >
          Edmunds Expert Rating
        </h2>
        <EdmundsTestedBadge />
      </div>
      <ArticleAuthor
        byline="Driven, tested, rated by automotive expert"
        tooltipProps={{
          suffix: (
            <em className="d-inline-block mt-1">
              Rated for you by{' '}
              <Link
                to="/car-reviews/features/how-we-test-cars-and-trucks.html"
                className="text-underline text-primary-darker"
                data-tracking-id="view_content"
                data-tracking-parent="edm-entry-review-scorecard" // for correct tracking
                rel="nofollow"
              >
                America&apos;s best test team
              </Link>
              .
            </em>
          ),
          trackingParent: 'edm-entry-review-scorecard',
          trackingSubaction: 'view_tooltip_scorecard_author',
        }}
        author={ratedBy}
        className="medium align-items-center mb-1"
        lazyLoadImage
        showBioTooltip
      />
      <div
        className={classnames('d-flex align-items-center px-1 py-1 rounded-8', {
          'flex-column flex-md-row bg-white mb-1': !isLargeHeadings,
          'bg-cool-gray-100 gap-1': isLargeHeadings,
        })}
      >
        <RatingBadge
          className={classnames({
            'ps-0_75 pe-1_25 mb-2 mb-md-0': !isLargeHeadings,
            'px-0_25 px-md-1_25': isLargeHeadings,
          })}
          badgeContainerClassName="ps-1_5 pe-1_5"
          ratingScale={scale}
          editorialRating={overallRating}
          isScorecardV4={isScorecardV4}
          ratingVersion={ratingsV4?.ratingVersion || scorecardVersion}
          showExplanationTooltip={hasExplanation({ isUsed: params?.isUsed, year })}
          hideTestedText
        />
        <p className="size-16 mb-0">
          We drove the {vehicleName} on our test track and on real roads. Our Edmunds team conducts rigorous independent
          vehicle testing, driving 500,000 miles a{' '}
          <span className="text-nowrap">
            year.
            <PopoverItem
              iconClassName="medium"
              openTrackingParams={{
                subaction_name: 'view_test_team_tooltip',
                creative_id: 'edm-entry-review-scorecard',
              }}
              screenReaderTooltipLabel="Edmunds test team"
            >
              The Edmunds Vehicle Testing Team evaluates a fresh batch of vehicles every week, pairing objective
              assessments at our test track with real-world driving on city streets, freeways and winding roads. The
              data we gather results in our Expert Ratings. They’re based on 30-plus scores that cover every aspect of
              the automotive experience.
            </PopoverItem>
          </span>
        </p>
      </div>
    </ScrollElement>
  );

  return (
    <ScrollElement
      id={MENU_HASH.RATING_SCORECARD}
      className={classnames('rating-scorecard mt-2 mb-2', {
        'bg-cool-gray-90 rounded-12 px-1 py-1': !isLargeHeadings,
        'd-flex flex-column gap-2': isLargeHeadings,
      })}
      data-tracking-parent="edm-entry-review-scorecard"
    >
      {renderMap.map(({ name, key, question, score, text, ratingSubSections }, sectionIndex) => {
        if (key === VERDICT_KEY) {
          return ourVerdict;
        }

        const ratingScore = score || ratings?.[key]?.rating;
        if (!ratingScore) {
          return null;
        }

        const ratingQuestion = get(ratings, `[${key}].title`) || question;
        const ratingText = text || get(ratings, `[${key}].text`);
        const ratingEmbeds = get(ratings, `[${key}].embeds`);

        return (
          <Fragment key={`scorecard-${key}`}>
            {key === 'utility' && (
              <PricingAd
                mobile={isMobile}
                position={'1'}
                showHorizontalLayout
                showOnlySiteServed
                wrapperClass="mt-0_5"
              />
            )}
            <RatingScorecardSection
              vehicleParams={vehicleParams}
              className={classnames({ 'mt-0_5': sectionIndex && !isLargeHeadings })}
              sectionKey={key}
              name={name}
              scale={scale}
              rating={ratingScore}
              content={`${ratingQuestion ? `${ratingQuestion} ` : ''}${ratingText}`}
              embeds={ratingEmbeds}
              ratingSubSections={ratingSubSections}
              isMobile={isMobile}
              isOpen={expandedEntryId === `ratings-${key}`}
              isScorecardV4={isScorecardV4}
              scorecardVersion={scorecardVersion}
              embedInteriorPhoto={embedInteriorPhoto}
              isLargeHeadings={isLargeHeadings}
            />
          </Fragment>
        );
      })}
    </ScrollElement>
  );
}

RatingScorecard.propTypes = {
  params: CorePageParams,
  vehicleParams: PropTypes.shape({
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    submodelName: PropTypes.string,
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    year: PropTypes.string,
  }),
  ratings: EditorialRatingsEntities,
  ratingsV4: EditorialRatingsV4Entities,
  scale: PropTypes.number,
  edmundsTypeCategories: PropTypes.objectOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  ratedBy: EditorialReviewEntities.AuthorData,
  expandedEntryId: PropTypes.string,
  embedInteriorPhoto: PropTypes.bool,
  scorecardVersion: PropTypes.string,
  hasCarryoverDisclaimer: PropTypes.bool,
  isScorecardV4: PropTypes.bool,
  isMobile: PropTypes.bool,
  isLargeHeadings: PropTypes.bool,
};
